import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, ClipboardList, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  completed: "bg-green-500/10 text-green-400 border-green-500/30",
  archived: "bg-zinc-500/10 text-zinc-500 border-zinc-500/30",
};

export default function Assessments() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "assessor"].includes(user?.role);
  const [params] = useSearchParams();
  const clientFilter = params.get("client");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false });
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const url = clientFilter ? `/assessments?client_id=${clientFilter}` : "/assessments";
      const [{ data }, c] = await Promise.all([
        api.get(url), api.get("/clients"),
      ]);
      setItems(data);
      setClients(c.data);
      if (canEdit) {
        try { const u = await api.get("/users"); setUsers(u.data); } catch {}
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [clientFilter]);

  const filtered = items.filter((a) =>
    [a.name, a.client_name, a.assessor_name, a.status].join(" ").toLowerCase().includes(search.toLowerCase())
  );

  const remove = async (id) => {
    if (!confirm("Delete assessment and all responses?")) return;
    try {
      await api.delete(`/assessments/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="assessments-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">CSF 2.0 Engagements</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Assessments</h1>
          <p className="text-sm text-zinc-500 mt-1">Run, score, and track maturity assessments.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setDialog({ open: true })} data-testid="new-assessment-button" className="bg-white text-black hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-1.5" /> New Assessment
          </Button>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assessments..."
            data-testid="assessments-search"
            className="pl-10 bg-[#121212] border-zinc-800" />
        </div>
        <div className="text-xs text-zinc-500 mono">{filtered.length} / {items.length}</div>
      </div>

      <div className="mt-4 rounded-md border border-zinc-800 bg-[#121212] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm" data-testid="assessments-empty">
            <ClipboardList className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
            No assessments yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="assessments-table">
            <thead className="bg-[#0A0A0A]">
              <tr>
                <th className="text-left px-4 py-3 label-uppercase">Assessment</th>
                <th className="text-left px-4 py-3 label-uppercase">Client</th>
                <th className="text-left px-4 py-3 label-uppercase">Date</th>
                <th className="text-left px-4 py-3 label-uppercase">Assessor</th>
                <th className="text-left px-4 py-3 label-uppercase">Status</th>
                <th className="text-right px-4 py-3 label-uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`assessment-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/assessments/${a.id}`} className="text-white font-medium hover:underline">{a.name}</Link>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">
                    <span>{a.client_name}</span>
                    <span className="block text-[10px] text-zinc-500 mt-0.5">{a.client_industry}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 mono text-xs">{a.assessment_date}</td>
                  <td className="px-4 py-3 text-zinc-300">{a.assessor_name}</td>
                  <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[a.status]} text-[10px]`}>{a.status.replace("_", " ").toUpperCase()}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/assessments/${a.id}`} className="px-2 py-1 text-xs text-zinc-300 hover:text-white inline-flex items-center gap-1" data-testid={`open-assessment-${a.id}`}>
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                      {canEdit && (
                        <button onClick={() => remove(a.id)} className="p-1.5 rounded hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`delete-assessment-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewAssessmentDialog open={dialog.open} onClose={() => setDialog({ open: false })} clients={clients} users={users} onSaved={load} />
    </div>
  );
}

function NewAssessmentDialog({ open, onClose, clients, users, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    client_id: "", name: "", assessment_date: new Date().toISOString().slice(0, 10),
    assessor_id: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        client_id: clients[0]?.id || "",
        name: `CSF 2.0 Assessment — ${new Date().toLocaleDateString()}`,
        assessment_date: new Date().toISOString().slice(0, 10),
        assessor_id: users.find((u) => u.role !== "client_viewer")?.id || user?.id || "",
        notes: "",
      });
    }
  }, [open, clients, users, user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/assessments", form);
      toast.success("Assessment created");
      onSaved();
      onClose();
      window.location.href = `/assessments/${data.id}`;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-lg" data-testid="new-assessment-dialog">
        <DialogHeader><DialogTitle>New Assessment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="label-uppercase">Client</Label>
            <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} required data-testid="assessment-client-select"
              className="mt-1.5 w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm">
              <option value="">Select…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="label-uppercase">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="assessment-name-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Date</Label>
              <Input type="date" value={form.assessment_date} onChange={(e) => setForm({ ...form, assessment_date: e.target.value })} data-testid="assessment-date-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
            </div>
            <div>
              <Label className="label-uppercase">Assessor</Label>
              <select value={form.assessor_id} onChange={(e) => setForm({ ...form, assessor_id: e.target.value })} required data-testid="assessment-assessor-select"
                className="mt-1.5 w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm">
                <option value="">Select…</option>
                {users.filter((u) => u.role !== "client_viewer").map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="label-uppercase">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
          <DialogFooter className="gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="assessment-create-submit" className="bg-white text-black hover:bg-zinc-200">
              {busy ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
